/**
 * ColorModeStore - Manages light/dark mode state
 *
 * Provides a simple Zustand store for color mode management with AsyncStorage
 * persistence. Use this to toggle between light and dark modes across the app.
 */

import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useColorScheme } from 'react-native';
import React from 'react';
import log from '../utils/logger';

// For backward compatibility, export React

const COLOR_MODE_KEY = '@flare/colorMode';

export type ColorMode = 'light' | 'dark' | 'auto';

interface ColorModeState {
  colorMode: ColorMode;
  systemColorMode: 'light' | 'dark';
  effectiveColorMode: 'light' | 'dark';
  setColorMode: (mode: ColorMode) => Promise<void>;
  loadColorMode: () => Promise<void>;
}

export const useColorModeStore = create<ColorModeState>((set, get) => ({
  colorMode: 'light',
  systemColorMode: 'light',
  effectiveColorMode: 'light',

  setColorMode: async (mode: ColorMode) => {
    try {
      await AsyncStorage.setItem(COLOR_MODE_KEY, mode);
      const { systemColorMode } = get();
      const effectiveColorMode = mode === 'auto' ? systemColorMode : mode;
      set({ colorMode: mode, effectiveColorMode });
    } catch (error) {
      log.error('Failed to save color mode:', error);
    }
  },

  loadColorMode: async () => {
    try {
      const savedMode = (await AsyncStorage.getItem(COLOR_MODE_KEY)) as ColorMode | null;
      const mode = savedMode || 'light';
      const { systemColorMode } = get();
      const effectiveColorMode = mode === 'auto' ? systemColorMode : mode;
      set({ colorMode: mode, effectiveColorMode });
    } catch (error) {
      log.error('Failed to load color mode:', error);
    }
  },
}));

/**
 * Hook to track system color scheme changes
 * Call this in your root component to keep systemColorMode in sync
 */
export const useSystemColorSchemeTracker = () => {
  const systemColorScheme = useColorScheme();

  React.useEffect(() => {
    const systemMode = systemColorScheme || 'light';
    useColorModeStore.setState((state) => ({
      systemColorMode: systemMode,
      effectiveColorMode: state.colorMode === 'auto' ? systemMode : state.effectiveColorMode,
    }));
  }, [systemColorScheme]);

  React.useEffect(() => {
    useColorModeStore.getState().loadColorMode();
  }, []);
};
